/**
 * Color utility functions for event styling
 */

/**
 * Darkens a hex color and applies opacity
 * Used for event type background colors
 * 
 * @param hexColor - Hex color string (e.g., '#FF0000')
 * @param darkenAmount - Amount to darken (0-255)
 * @param alpha - Alpha opacity (0-1)
 * @returns RGBA color string
 * 
 * @example
 * ```ts
 * darkenHexColorWithOpacity('#FF0000', 80, 0.1)
 * // Returns 'rgba(175, 0, 0, 0.1)'
 * ```
 */
export function darkenHexColorWithOpacity(
  hexColor: string,
  darkenAmount: number,
  alpha: number
): string {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Darken by subtracting darkenAmount (clamped to 0-255)
  const darkenedR = Math.max(0, r - darkenAmount);
  const darkenedG = Math.max(0, g - darkenAmount);
  const darkenedB = Math.max(0, b - darkenAmount);

  // Return RGBA string with alpha
  return `rgba(${darkenedR}, ${darkenedG}, ${darkenedB}, ${alpha})`;
}

/**
 * Convert hex color to RGB
 * 
 * @param hexColor - Hex color string
 * @returns Object with r, g, b values
 */
export function hexToRgb(hexColor: string): { r: number; g: number; b: number } | null {
  const hex = hexColor.replace('#', '');
  
  if (hex.length !== 6) {
    return null;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return { r, g, b };
}

/**
 * Get contrasting text color (black or white) for a background color
 * 
 * @param hexColor - Hex color string
 * @returns 'black' or 'white'
 */
export function getContrastColor(hexColor: string): 'black' | 'white' {
  const rgb = hexToRgb(hexColor);
  
  if (!rgb) {
    return 'black';
  }

  // Calculate relative luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;

  return luminance > 0.5 ? 'black' : 'white';
}
